/**
 * Preset Schemas — frontend mirror of the backend `TYPE_MASTER_TEMPLATES`.
 *
 * Purely position-based classification (per user directive d.1 for the
 * Unified Category Field UI rollout). Identity fields drive naming and
 * are treated as REQUIRED; every other preset field is SUPPORTING and
 * OPTIONAL; any field whose name is not in either list AND was
 * previously seeded as `master=true` is a LEGACY field held on the
 * category from an older preset revision.
 *
 * These arrays MUST stay in lock-step with `TYPE_MASTER_TEMPLATES` in
 * `/app/backend/server.py`. When the backend template changes, update
 * the corresponding list here in the same commit.
 */

export const PRESET_SCHEMAS = {
  wine: {
    identity: ["Producer", "Region", "Appellation", "Grape(s)", "ABV", "Vintage", "Bottle Size", "Country", "Sub-Region", "Type", "Classification", "Cuvée"],
    supporting: ["Drink From", "Drink To", "Tasting Notes", "Personal Notes", "Storage Location", "Purchase Date", "Purchase Source", "Quantity", "Case Size"],
  },
  whisky: {
    // Phase 5 (2026-02) — Whisky identity is now:
    //   Distillery · Whisky Type · Age / Classification · ABV · Bottle Size
    // Every other backend Whisky template field is listed in
    // `supporting` so it renders inside the Additional Item Details
    // grid instead of being classified as legacy. `Age / Classification`
    // itself is not in the backend template — the frontend renders
    // it as a synthetic identity field so no schema change is needed.
    //
    // Names below mirror `TYPE_MASTER_TEMPLATES.whisky` in
    // /app/backend/server.py verbatim (including spaces in
    // "Batch / Barrel Number").
    identity: [
      "Distillery", "Whisky Type", "Age / Classification", "ABV", "Bottle Size",
    ],
    supporting: [
      "Item Name",
      "Tasting Notes",
      "Personal Notes", "Purchase Date", "Purchase Source",
      "Storage Location",
    ],
  },
  spirits: {
    // Phase 2 (2026-02-14 rebuild) — Spirits identity is:
    //   Brand · Spirit Type · Age · ABV · Bottle Size
    // `Item Name` is auto-composed from the identity fields and lives
    // in the supporting list so it renders alongside the other
    // metadata rows (matches Wine/Whisky). Every other backend
    // template field is listed in `supporting` so it renders inside
    // Additional Item Details instead of being classified as legacy.
    //
    // Names mirror `TYPE_MASTER_TEMPLATES.spirits` in
    // /app/backend/server.py verbatim.
    identity: [
      "Brand", "Spirit Type", "Age Classification", "ABV", "Bottle Size",
    ],
    supporting: [
      "Item Name",
      "Distillery / Producer", "Cask Type / Finish",
      "Tasting Notes",
      "Personal Notes", "Purchase Date", "Purchase Source",
      "Storage Location",
    ],
  },
};

/**
 * classifyPresetField(field, collectionTypeId) → "identity" | "supporting" | "legacy"
 *
 *   identity   — field name is in the preset's identity list
 *                (required, drives naming)
 *   supporting — field name is in the preset's supporting list
 *                OR the collection type has no preset (e.g. "general",
 *                "custom", "collectables") in which case every field
 *                is optional metadata
 *                OR the field is a user-added custom row (master=false)
 *   legacy     — master field NOT in either preset list (was seeded by
 *                an older preset revision; keep it, but treat it as
 *                read-only for the Legacy Fields section)
 */
export function classifyPresetField(field, collectionTypeId) {
  const preset = PRESET_SCHEMAS[collectionTypeId];
  const name = field?.field_name || "";
  if (preset) {
    if (preset.identity.includes(name)) return "identity";
    if (preset.supporting.includes(name)) return "supporting";
    // Master field NOT in either list = legacy row from older preset.
    if (field?.master) return "legacy";
  }
  return "supporting";
}

/**
 * getIdentityOrder(collectionTypeId) → string[]  (empty for non-preset types)
 * Convenience for iterating identity fields in preset-defined order.
 */
export function getIdentityOrder(collectionTypeId) {
  return PRESET_SCHEMAS[collectionTypeId]?.identity || [];
}
